import { format, parse } from 'date-fns'
import * as Clipboard from 'expo-clipboard'
import { Box, Pressable, Row, Text, Center } from 'native-base'
import { useCallback, useMemo } from 'react'
import { ImageStyle } from 'react-native'

import { Icon } from './Icon'
import { RemoteImage } from './RemoteImage'

import { BoxWithShadow } from '~components/atoms'
import { useToast, useTranslation } from '~hooks'
import { PurchaseHistoryItem } from '~types/user'

export const PURCHASE_TILE_HEIGHT = 90

type PurchaseHistoryTileProps = PurchaseHistoryItem & {
  onPress?: () => void
}

export const PurchaseHistoryTile = ({
  description,
  date,
  points,
  code,
  icon,
  type,
  offerId,
  offerTitle,
  cartItems,
  offer,
  redeemedOn,
  image,
  discountValue,
  discountType,
  discountText,
  onPress,
}: PurchaseHistoryTileProps): JSX.Element => {
  const { t } = useTranslation()
  const { showToast } = useToast()

  const isEarned = useMemo(() => icon === 'earned', [icon])
  const isRedemptionHistory = useMemo(() => !!redeemedOn, [redeemedOn])
  const showCode = useMemo(
    () => (type === 'discount' && code) || isRedemptionHistory,
    [type, code, isRedemptionHistory]
  )

  // For redemption history, use image field; otherwise use offer.image
  const displayImage = useMemo(() => {
    if (isRedemptionHistory && image) return image
    return offer?.image
  }, [isRedemptionHistory, image, offer])

  // Format date as "1 Dec 2025" for both redemption history and points history
  const formattedDate = useMemo(() => {
    const dateToFormat = redeemedOn || date
    if (!dateToFormat) return ''
    try {
      // Parse date in format "MM/dd/yyyy" and format as "d MMM yyyy"
      const parsedDate = parse(dateToFormat, 'MM/dd/yyyy', new Date())
      return format(parsedDate, 'd MMM yyyy')
    } catch {
      return dateToFormat
    }
  }, [redeemedOn, date])

  const iconBgColor = useMemo(() => (isEarned ? 'actionBlue' : 'error.500'), [isEarned])
  const pointsBgColor = useMemo(() => (points > 0 ? 'success.500' : 'error.500'), [points])
  const pointsText = useMemo(() => {
    const sign = points > 0 ? '+' : ''
    return `${sign}${points} PTS`
  }, [points])

  // Format discount value for redemption history
  const discountDisplay = useMemo(() => {
    if (!isRedemptionHistory || !discountValue) return null
    if (discountText) return discountText
    if (discountType === 1) {
      return `${discountValue}% off`
    }
    return `₹${discountValue} off`
  }, [isRedemptionHistory, discountValue, discountType, discountText])

  const imageStyle: ImageStyle = useMemo(
    () => ({
      width: '100%',
      height: '100%',
      borderRadius: 8,
    }),
    []
  )

  const descriptionParts = useMemo(() => {
    if (type === 'reward' && cartItems && cartItems.length > 1) {
      const firstItemName = cartItems[0]?.name || description
      const remainingCount = cartItems.length - 1
      return {
        firstItemName,
        remainingCount,
        showMore: true,
      }
    }
    return {
      firstItemName: description,
      remainingCount: 0,
      showMore: false,
    }
  }, [type, cartItems, description])

  const copyToClipboard = useCallback(
    async (e?: any) => {
      if (!code) return
      e?.stopPropagation?.()
      await Clipboard.setStringAsync(code)
      showToast({ description: t('toast_messages.coupon_copied'), variant: 'info' })
    },
    [code, t, showToast]
  )

  const handleTilePress = useCallback(() => {
    if (onPress && offerId) {
      onPress()
    }
  }, [onPress, offerId])

  return (
    <Pressable onPress={handleTilePress}>
      <BoxWithShadow rounded="lg" shadowSize="s" bg="white" p="4">
        <Row alignItems="flex-start" justifyContent="space-between">
          {/* Left side: Icon/Image and content */}
          <Row alignItems="flex-start" flex={1}>
            {/* Image for discount type or redemption history, Circular Icon for reward type */}
            {((type === 'discount' || isRedemptionHistory) && displayImage) ? (
              <Box w="16" h="16" mr="3" flexShrink={0} rounded="lg" overflow="hidden">
                <RemoteImage
                  alt="offer image"
                  resizeMode="cover"
                  style={imageStyle}
                  uri={displayImage}
                />
              </Box>
            ) : (
              <Center bg={iconBgColor} rounded="full" boxSize="10" mr="3" flexShrink={0}>
                <Icon name="links-line" size={20} color="white" />
              </Center>
            )}

            {/* Content */}
            <Box flex={1}>
              {isRedemptionHistory ? (
                <>
                  {/* Redemption History Layout */}
                  {offerTitle && (
                    <Text fontSize="sm" bold color="black" mb="1" numberOfLines={1}>
                      {offerTitle}
                    </Text>
                  )}
                  {code && (
                    <Pressable
                      onPress={copyToClipboard}
                      flexDirection="row"
                      alignItems="center"
                      mb="1"
                    >
                      <Text fontSize="xs" color="offMain" numberOfLines={1} flexShrink={1}>
                        {code}
                      </Text>
                      <Box ml="1">
                        <Icon name="file-copy-line" size={14} color="actionBlue" />
                      </Box>
                    </Pressable>
                  )}
                  {discountDisplay && (
                    <Text fontSize="xs" color="actionBlue" bold mb="1">
                      {discountDisplay}
                    </Text>
                  )}
                </>
              ) : (
                <>
                  {/* Points History Layout */}
                  <Row alignItems="center" flexWrap="wrap" mb="1">
                    <Text fontSize="sm" bold color="black">
                      {descriptionParts.firstItemName}
                    </Text>
                    {descriptionParts.showMore && (
                      <Box bg="blue.50" px="2" py="0.5" rounded="md" ml="2" mt="0.5">
                        <Text fontSize="2xs" bold color="actionBlue">
                          +{descriptionParts.remainingCount} more
                        </Text>
                      </Box>
                    )}
                  </Row>

                  {showCode && (
                    <Pressable
                      onPress={copyToClipboard}
                      flexDirection="row"
                      alignItems="center"
                      mb="1"
                    >
                      <Text fontSize="xs" color="offMain" numberOfLines={1} flexShrink={1}>
                        {code}
                      </Text>
                      <Box ml="1">
                        <Icon name="file-copy-line" size={14} color="actionBlue" />
                      </Box>
                    </Pressable>
                  )}

                  <Text fontSize="xs" color="offMain">
                    {formattedDate}
                  </Text>
                </>
              )}
            </Box>
          </Row>

          {/* Right side: Redeemed date for redemption history, Points badge for points history */}
          {isRedemptionHistory ? (
            formattedDate && (
              <Text fontSize="xs" color="offMain" ml="3" flexShrink={0}>
                {formattedDate}
              </Text>
            )
          ) : (
            <Center bg={pointsBgColor} px="3" py="1.5" rounded="md" ml="3" flexShrink={0}>
              <Text fontSize="xs" bold color="white">
                {pointsText}
              </Text>
            </Center>
          )}
        </Row>
      </BoxWithShadow>
    </Pressable>
  )
}
